import {
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { useCallback, useEffect, useState } from "react";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import {
  OrgSubjectsService,
  OrganizationSubjectAdminResponse,
  OrganizationSubjectPublicResponse,
  PaymentsService,
  StudentToOrgTransactionType,
  PaymentMethodType,
  StudentAdminResponse,
} from "client/openapi";
import { APIResponse, PageStatus } from "types";
import Notifications from "util/notifications";
import CurrencyInput from "react-currency-input-field";

import "./index.css";

interface Credits {
  orgSubjectId: number;
  studentSubjectId: number;
  credits: number;
}

interface CreditAdjustment {
  orgSubjectId: number;
  studentSubjectId: number;
  currentAmount: number;
  adjustedAmount: number;
  reason: string;
}

// Sentinel to represent a student's general credits
const GENERAL_CREDITS_ID = -1;
const GENERAL_CREDITS_LABEL = "General-Use Credits";

export default function CreditDetails({
  orgId,
  student,
  currentStudentCredits,
  generalCredits,
  onCreditsUpdated,
}: {
  orgId: number;
  student: StudentAdminResponse;
  currentStudentCredits: Credits[];
  generalCredits: number;
  onCreditsUpdated?: (
    creditChanges: { orgSubjectId: number; newAmount: number }[]
  ) => void;
}) {
  const [subjects, setSubjects] = useState<
    OrganizationSubjectAdminResponse[] | OrganizationSubjectPublicResponse[]
  >([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [status, setStatus] = useState<PageStatus>();
  const [error, setError] = useState<APIResponse>();
  const [creditAdjustments, setCreditAdjustments] = useState<
    CreditAdjustment[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const [maxValueError, setMaxValueError] = useState<string>("");

  const getAndSetSubjects = useCallback(async () => {
    setStatus(PageStatus.LOADING);
    OrgSubjectsService.getSubjectsByOrg({
      orgId: orgId,
    })
      .then((subjects) => {
        setSubjects(subjects);
        setStatus(PageStatus.SUCCESS);
      })
      .catch((error) => {
        setError(error);
        setStatus(PageStatus.ERROR);
        console.error(`Error (#${error.status}): ${error.message}`);
      });
  }, [orgId]);

  function handleClose() {
    setIsEditMode(false);
    setCreditAdjustments([]);
    setStatus(undefined);
    setMaxValueError("");
  }

  const handleEditModeToggle = () => {
    if (isEditMode) {
      // Cancel edit mode
      setIsEditMode(false);
      setCreditAdjustments([]);
      setMaxValueError("");
    } else {
      // Enter edit mode
      if (subjects.length === 0) {
        Notifications.error(
          "Please wait for subjects to load before editing credits."
        );
        return;
      }
      setIsEditMode(true);
      // Initialize credit adjustments with current values
      const subjectAdjustments = subjects.map((subject) => {
        const credit = currentStudentCredits.find(
          (credit) => credit.orgSubjectId === subject.id
        );
        return {
          orgSubjectId: subject.id,
          studentSubjectId: credit?.studentSubjectId || 0,
          currentAmount: credit?.credits || 0,
          adjustedAmount: credit?.credits || 0,
          reason: "",
        };
      });

      // Also include the student's general credits as its own editable line item
      const generalAdjustment: CreditAdjustment = {
        orgSubjectId: GENERAL_CREDITS_ID,
        studentSubjectId: GENERAL_CREDITS_ID,
        currentAmount: generalCredits,
        adjustedAmount: generalCredits,
        reason: "",
      };

      setCreditAdjustments([generalAdjustment, ...subjectAdjustments]);
    }
  };

  const handleCreditAdjustment = (orgSubjectId: number, value: string) => {
    // Only subject balances may go below zero. Billing never uses general
    // credits at or below $0, so a negative general balance could not be paid down.
    const allowNegative = orgSubjectId !== GENERAL_CREDITS_ID;
    const dollars = Number(value);
    const isNumber = value.trim() !== "" && !isNaN(dollars);

    // Check the limits ($100,000 either way)
    if (isNumber && dollars > 100000) {
      setMaxValueError(
        "Please enter a value less than or equal to $100,000.00"
      );
      return;
    }
    if (isNumber && allowNegative && dollars < -100000) {
      setMaxValueError(
        "Please enter a value greater than or equal to -$100,000.00"
      );
      return;
    }

    // Clear any previous max value error
    setMaxValueError("");

    // An empty box, a lone "-" or an invalid value means "no change": keep the
    // current amount rather than resetting to 0. Rounding to whole cents keeps
    // $19.99 at 1999 (19.99 * 100 is 1998.9999999999998).
    const isValid = isNumber && (allowNegative || dollars >= 0);

    setCreditAdjustments((prev) =>
      prev.map((adj) =>
        adj.orgSubjectId === orgSubjectId
          ? {
              ...adj,
              adjustedAmount: isValid
                ? Math.round(dollars * 100)
                : adj.currentAmount,
            }
          : adj
      )
    );
  };

  const handleReasonChange = (orgSubjectId: number, reason: string) => {
    setCreditAdjustments((prev) =>
      prev.map((adj) =>
        adj.orgSubjectId === orgSubjectId ? { ...adj, reason } : adj
      )
    );
  };

  const getProposedChanges = () => {
    return creditAdjustments
      .filter((adj) => adj.adjustedAmount !== adj.currentAmount)
      .map((adj) => ({
        orgSubjectId: adj.orgSubjectId,
        studentSubjectId: adj.studentSubjectId,
        subjectName:
          adj.orgSubjectId === GENERAL_CREDITS_ID
            ? GENERAL_CREDITS_LABEL
            : subjects.find((s) => s.id === adj.orgSubjectId)?.name || "",
        difference: adj.adjustedAmount - adj.currentAmount,
        newBalance: adj.adjustedAmount,
        reason: adj.reason,
      }));
  };

  const hasValidChanges = () => {
    const changes = getProposedChanges();
    return (
      changes.length > 0 &&
      changes.every((change) => change.reason.trim() !== "") &&
      maxValueError === ""
    );
  };

  const handleSave = async () => {
    if (!hasValidChanges()) return;

    setIsSaving(true);
    const changes = getProposedChanges();

    try {
      // Create transactions for each change
      const transactionPromises = changes.map(async (change) => {
        const adjustment = creditAdjustments.find(
          (adj) => adj.orgSubjectId === change.orgSubjectId
        );

        if (!adjustment) return;

        const transactionType =
          change.difference > 0
            ? StudentToOrgTransactionType.CREDITS_ADDED_MANUALLY
            : StudentToOrgTransactionType.CREDITS_DEDUCTED_MANUALLY;

        return PaymentsService.createStudentToOrgPayment({
          requestBody: {
            amount: Math.abs(change.difference),
            org_id: orgId,
            student_id: student.id,
            student_subject_id:
              change.orgSubjectId !== GENERAL_CREDITS_ID
                ? change.studentSubjectId
                : undefined,
            payment_method_type: PaymentMethodType.TADPOLE_CREDITS,
            reason: `${change.subjectName}: ${change.reason}`,
            student_to_org_transaction_type: transactionType,
          },
        });
      });

      await Promise.all(transactionPromises);

      // Calculate the new credit amounts and update parent component
      const creditChanges = changes.map((change) => ({
        orgSubjectId: change.orgSubjectId,
        newAmount:
          change.difference +
          (change.orgSubjectId === GENERAL_CREDITS_ID
            ? generalCredits
            : currentStudentCredits.find(
                (c) => c.orgSubjectId === change.orgSubjectId
              )?.credits || 0),
      }));

      Notifications.success("Credit adjustments saved successfully!");
      setIsEditMode(false);
      setCreditAdjustments([]);

      // Notify parent component to update credits with new amounts
      if (onCreditsUpdated) {
        onCreditsUpdated(creditChanges);
      }
    } catch (err: any) {
      setError(err);
      Notifications.error(err?.message || "Failed to save credit adjustments");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    getAndSetSubjects();
  }, [getAndSetSubjects]);

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  return (
    <DialogContent className="credit-details-dialog" onClose={handleClose}>
      <div className="mt-3 font-semibold text-center header text-lg">
        Credits on File
      </div>

      {subjects.length > 0 && (
        <table className="table mt-3">
          <thead className="text-left text-xs text-gray-500">
            <tr>
              <th className="w-1/3">Category</th>
              <th className="w-1/3">
                {student.first_name}'s Credit Balance ($)
              </th>
              {isEditMode && <th className="w-1/3">Adjusted Balance ($)</th>}
            </tr>
          </thead>
          <tbody>
            <tr
              key="general-credits-row"
              className="student-subject-item odd:bg-gray-50 even:bg-white"
            >
              <td>
                <div
                  title={GENERAL_CREDITS_LABEL}
                  className="student-subject-title"
                >
                  {GENERAL_CREDITS_LABEL}
                </div>
              </td>
              <td>
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(generalCredits / 100)}
              </td>
              {isEditMode && (
                <td>
                  <CurrencyInput
                    defaultValue={generalCredits / 100}
                    max={100000}
                    className="input w-full h-8 text-sm"
                    prefix="$"
                    decimalsLimit={2}
                    allowNegativeValue={false}
                    onValueChange={(value) =>
                      handleCreditAdjustment(GENERAL_CREDITS_ID, value || "")
                    }
                  />
                </td>
              )}
            </tr>

            {subjects
              .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
              .map((s) => {
                const currentAmount =
                  currentStudentCredits.find(
                    (credit) => credit.orgSubjectId === s.id
                  )?.credits || 0;

                return (
                  <tr
                    key={s.id}
                    className="student-subject-item odd:bg-gray-50 even:bg-white"
                  >
                    <td>
                      <div title={s.name} className="student-subject-title">
                        {s.name}
                      </div>
                    </td>
                    <td>
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                      }).format(currentAmount / 100)}
                    </td>
                    {isEditMode && (
                      <td>
                        <CurrencyInput
                          defaultValue={currentAmount / 100}
                          max={100000}
                          className="input w-full h-8 text-sm"
                          prefix="$"
                          decimalsLimit={2}
                          allowNegativeValue={true}
                          onValueChange={(value) =>
                            handleCreditAdjustment(s.id, value || "")
                          }
                        />
                      </td>
                    )}
                  </tr>
                );
              })}
          </tbody>
        </table>
      )}

      {maxValueError && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{maxValueError}</p>
        </div>
      )}

      {isEditMode && getProposedChanges().length > 0 && (
        <div className="mt-4 p-4 border border-gray-300 rounded-lg">
          <h4 className="font-semibold mb-3">Proposed Changes</h4>
          <div className="space-y-3">
            {getProposedChanges().map((change, index) => (
              <div key={index}>
                <div className="flex items-center gap-3">
                  <span className="font-medium min-w-[120px]">
                    {change.subjectName}:
                  </span>
                  <span
                    className={`font-semibold ${
                      change.difference > 0 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {change.difference > 0 ? "+" : ""}
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(change.difference / 100)}
                  </span>
                  <input
                    type="text"
                    className="flex-1 text-sm border border-gray-300 rounded py-1 px-2"
                    placeholder="Reason for this change (required)"
                    value={change.reason}
                    onChange={(e) => {
                      handleReasonChange(change.orgSubjectId, e.target.value);
                    }}
                  />
                </div>
                {change.newBalance < 0 ? (
                  <p className="mt-1 text-xs text-gray-500">
                    New balance{" "}
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(change.newBalance / 100)}
                    . This shows as owed (Owes, Unpaid Tutoring). No one is
                    charged or refunded.
                  </p>
                ) : (
                  change.difference < 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      No one is charged or refunded.
                    </p>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <DialogActions>
        {isEditMode ? (
          <>
            <Button
              color={ButtonColor.GRAY}
              size={ButtonSize.SMALL}
              onClick={handleEditModeToggle}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              color={
                !hasValidChanges() || isSaving
                  ? ButtonColor.GRAY
                  : ButtonColor.GREEN
              }
              size={ButtonSize.SMALL}
              onClick={handleSave}
              disabled={!hasValidChanges() || isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </>
        ) : (
          <>
            <DialogClose asChild>
              <DialogAction
                color={ButtonColor.PURPLE}
                onClick={() => handleClose()}
              >
                Close
              </DialogAction>
            </DialogClose>
            <Button
              color={ButtonColor.DARK_BLUE}
              size={ButtonSize.SMALL}
              onClick={handleEditModeToggle}
              disabled={status === PageStatus.LOADING || subjects.length === 0}
            >
              {status === PageStatus.LOADING ? "Loading..." : "Edit"}
            </Button>
          </>
        )}
      </DialogActions>
    </DialogContent>
  );
}
